import { Link } from "wouter";
import { MapPin, Mail, MessageCircle } from "lucide-react";
import { makeToSlug } from "@/lib/makeSlug";
import { POPULAR_MAKES } from "@shared/marketplace";
import { trpc } from "@/lib/trpc";

export default function Footer() {
  const stats = trpc.publicStats.useQuery(undefined, {
    staleTime: 5 * 60_000,
  });

  return (
    <footer
      className="relative overflow-hidden pb-20 md:pb-0"
      style={{
        background:
          "linear-gradient(145deg, oklch(0.22 0.02 250) 0%, oklch(0.18 0.015 240) 50%, oklch(0.20 0.025 220) 100%)",
      }}
    >
      <div className="container relative py-10">
        {/* Live marketplace pulse, real numbers, not decoration.
            Wrapper has fixed height so the async stats fetch can't shift the
            footer grid when it resolves. This was responsible for ~95% of the
            page's CLS (0.32 → reserved → ~0.02). */}
        <div className="flex items-center justify-center gap-2 text-[11px] text-white/50 tracking-wider mb-8 h-[18px]">
          {stats.data ? (
            <>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>
                {stats.data.totalProducts.toLocaleString()} parts
                {" · "}
                {stats.data.totalVendors.toLocaleString()} vendors
                {" · "}
                {stats.data.totalCategories} categories
              </span>
            </>
          ) : null}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Brand + contact */}
          <div className="col-span-2 md:col-span-1 space-y-4">
            <div className="flex items-center min-h-10">
              <img
                src="/voom-logo.png"
                alt="Voom"
                width="80"
                height="40"
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-sm text-white/70 leading-relaxed tracking-wide">
              Ghana's auto parts marketplace. Verified vendors, real prices,
              WhatsApp in one tap.
            </p>
            <address className="not-italic space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-white/40" />
                <span className="text-white/60 tracking-wide text-xs">
                  Abossey Okai, Accra
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-white/40" />
                <a
                  href="mailto:sales@voomparts.com"
                  className="text-white/60 tracking-wide text-xs hover:text-white transition-colors no-underline"
                >
                  sales@voomparts.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MessageCircle className="h-3.5 w-3.5 text-white/40" />
                <a
                  href="https://chat.whatsapp.com/IZzVqhrkhxQ04PyznlWD4N"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 tracking-wide text-xs hover:text-white transition-colors no-underline"
                >
                  WhatsApp community
                </a>
              </div>
            </address>
          </div>

          {/* Browse */}
          <div>
            <h4 className="font-medium text-white/80 mb-4 text-xs tracking-wider uppercase">
              Browse
            </h4>
            <div className="space-y-2.5">
              <FooterLink href="/products">All Parts</FooterLink>
              <FooterLink href="/vendors">Vendors</FooterLink>
              <FooterLink href="/categories">Categories</FooterLink>
              <FooterLink href="/requests">Request a Part</FooterLink>
            </div>
          </div>

          {/* Popular Makes */}
          <div>
            <h4 className="font-medium text-white/80 mb-4 text-xs tracking-wider uppercase">
              Top Makes
            </h4>
            <div className="space-y-2.5">
              {POPULAR_MAKES.slice(0, 6).map(make => (
                <FooterLink key={make} href={`/parts/${makeToSlug(make)}`}>
                  {make}
                </FooterLink>
              ))}
            </div>
          </div>

          {/* For Vendors */}
          <div>
            <h4 className="font-medium text-white/80 mb-4 text-xs tracking-wider uppercase">
              Sell on Voom
            </h4>
            <div className="space-y-2.5">
              <FooterLink href="/vendor/register">Register</FooterLink>
              <FooterLink href="/vendor/dashboard">Dashboard</FooterLink>
              <FooterLink href="/pricing">Plans & Pricing</FooterLink>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[10px] text-white/40 tracking-wider">
            &copy; {new Date().getFullYear()} Voom Parts Inc
          </p>
          <p className="text-[10px] text-white/40 tracking-wider">
            Built in Accra for every mechanic and vendor.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="block text-xs text-white/60 hover:text-white transition-colors no-underline tracking-wide"
    >
      {children}
    </Link>
  );
}
